import { useState, useCallback } from 'react';
import { superadminService, User, CreateUserDTO, UpdateUserDTO } from '../services/superadmin.service';
import { useToast } from '@/components/ui/use-toast';

export function useSuperadmin() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<{ users: number; tenants: number; clientes: number; contratos: number; } | null>(null);
  const { toast } = useToast();

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await superadminService.getUsers();
      setUsers(data);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os usuários',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const createUser = useCallback(async (userData: CreateUserDTO) => {
    try {
      setLoading(true);
      await superadminService.createUser(userData);
      toast({
        title: 'Sucesso',
        description: 'Usuário criado com sucesso',
      });
      await fetchUsers();
    } catch (error: any) {
      console.error('Erro ao criar usuário:', error);
      toast({
        title: 'Erro',
        description: `Não foi possível criar o usuário: ${error.message || 'Erro desconhecido'}`,
        variant: 'destructive',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchUsers, toast]);

  const updateUser = useCallback(async (userId: any, userData: UpdateUserDTO) => {
    try {
      setLoading(true);
      await superadminService.updateUser(userId, userData);
      toast({
        title: 'Sucesso',
        description: 'Usuário atualizado com sucesso',
      });
      await fetchUsers();
    } catch (error: any) {
      console.error('Erro ao atualizar usuário:', error);
      toast({
        title: 'Erro',
        description: `Não foi possível atualizar o usuário: ${error.message || 'Erro desconhecido'}`,
        variant: 'destructive',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchUsers, toast]);

  const checkUserDependencies = useCallback(async (userId: string) => {
    try {
      return await superadminService.checkUserDependencies(userId);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível verificar as dependências do usuário',
        variant: 'destructive',
      });
      throw error;
    }
  }, [toast]);

  const deleteUser = useCallback(async (userId: string, forceUnlink: boolean = false) => {
    try {
      setLoading(true);
      await superadminService.deleteUser(userId, forceUnlink);
      toast({
        title: 'Sucesso',
        description: 'Usuário deletado com sucesso',
      });
      await fetchUsers();
    } catch (error: any) {
      console.error('Erro ao deletar usuário:', error);
      toast({
        title: 'Erro',
        description: `Não foi possível deletar o usuário: ${error.message || 'Erro desconhecido'}`,
        variant: 'destructive',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchUsers, toast]);

  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await superadminService.getDashboardStats();
      setStats(data);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as estatísticas',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  return {
    users,
    loading,
    stats,
    fetchUsers,
    createUser,
    updateUser,
    checkUserDependencies,
    deleteUser,
    fetchDashboardStats,
  };
} 